// src/frontend/js/modern/shared/store-helpers.js
//
// Base class for persistent store management.
// Provides shared CRUD, optimistic and sync helpers for all module stores.
import { stateCache } from '../core/state-cache.js';
import { displayStore } from '../core/storage/display-store.js';

// Fields that are list-specific (depend on which list the product is in).
// The server sometimes nests these inside the `display` object alongside
// shared product fields. We must promote them to the item level so they
// are persisted in StateCache (DisplayStore only keeps shared fields).
const LIST_SPECIFIC_FIELDS = [
	'remove_url', 'remove_nonce', 'wishlist_token',
	'list_name', 'item_id', 'wishlist_id'
];

export class BaseStore {
	constructor(moduleName, itemKey = 'product_id') {
		this.moduleName = moduleName;
		this.itemKey = itemKey;
	}

	get items() { return { get: () => stateCache.getItems(this.moduleName) }; }
	get count() { return { get: () => stateCache.getCount(this.moduleName) }; }

	hasItem(id) {
		const needle = String(id);
		return stateCache.getItems(this.moduleName).some(item => String(item[this.itemKey]) === needle);
	}

	getItem(id) {
		const needle = String(id);
		return stateCache.getItems(this.moduleName).find(item => String(item[this.itemKey]) === needle) || null;
	}

	findItem(predicate) { return stateCache.getItems(this.moduleName).find(predicate) || null; }

	getItemsBy(field, value) {
		const needle = String(value);
		return stateCache.getItems(this.moduleName).filter(item => String(item[field]) === needle);
	}

	addItem(id, data = {}) {
		if (this.hasItem(id)) return false;
		const newItem = { [this.itemKey]: id, _optimistic: true, _timestamp: Date.now(), ...data };
		stateCache.upsertItem(this.moduleName, String(id), newItem);
		return true;
	}

	removeItem(id) { return stateCache.removeItem(this.moduleName, String(id)); }
	updateItem(id, updates) { return stateCache.updateItem(this.moduleName, String(id), updates); }

	confirmAdd(id, serverData) {
		const { display, ...cleanData } = serverData || {};
		// Promote list-specific fields from display to cleanData if missing
		if (display) {
			for (const key of LIST_SPECIFIC_FIELDS) {
				if (display[key] !== undefined && cleanData[key] === undefined) {
					cleanData[key] = display[key];
				}
			}
		}
		stateCache.confirmItem(this.moduleName, String(id), cleanData);
	}

	rollbackAdd(id) { stateCache.rollbackItem(this.moduleName, String(id)); }

	getFullState() {
		return { items: stateCache.getItems(this.moduleName), count: stateCache.getCount(this.moduleName) };
	}

	clear() { stateCache.setModuleData(this.moduleName, [], 0); }

	/* ═══ Display Data (delegated to DisplayStore) ═══ */

	/**
	 * Store shared display data for a product within this module's namespace.
	 * RAM Map is updated synchronously; IDB write is fire-and-forget.
	 */
	setDisplayData(productId, data) {
		displayStore.set(this.moduleName, productId, data);
	}

	/**
	 * Synchronous read of display data from RAM cache.
	 * Returns null if not found.
	 */
	getDisplayData(productId) {
		return displayStore.get(this.moduleName, productId);
	}

	/**
	 * Batch-set display data extracted from sync/hydration items.
	 * Items format: [{ product_id, display: {...} }, ...]
	 */
	setBulkDisplayData(items) {
		if (!Array.isArray(items)) return;
		for (const item of items) {
			if (!item || item.product_id === undefined) continue;
			if (item.display && (item.display.name || item.display.image)) {
				this.setDisplayData(item.product_id, item.display);
			} else if (item.name || item.image) {
				this.setDisplayData(item.product_id, item);
			}
		}
	}

	/* ═══ Sync ═══ */

	/**
	 * Sync module data from server response.
	 * Strips bulky display objects from items BEFORE persisting
	 * and routes them to the IDB-backed DisplayStore.
	 */
	syncFromData(data) {
		let items = data.items || [];
		if (Array.isArray(items)) {
			items.forEach(item => {
				if (item?.display && item.product_id !== undefined) {
					displayStore.set(this.moduleName, item.product_id, item.display);
				}
			});
			items = items.map(item => {
				const { display, ...rest } = item;
				if (display) {
					for (const key of LIST_SPECIFIC_FIELDS) {
						if (display[key] !== undefined && rest[key] === undefined) {
							rest[key] = display[key];
						}
					}
				}
				return rest;
			});
		}
		stateCache.setModuleData(this.moduleName, items, data.count || items.length);
	}

	batchUpdate(updates) {
		const current = stateCache.getItems(this.moduleName);
		const updated = current.map(item => {
			const update = updates.find(u => String(u[this.itemKey]) === String(item[this.itemKey]));
			return update ? { ...item, ...update } : item;
		});
		stateCache.setModuleData(this.moduleName, updated, updated.length);
	}
}

/**
 * Normalize any data shape (Array | Object | null) into an Array.
 * Central helper — use EVERYWHERE an array is expected but the source
 * shape is uncertain.
 */
export function normalizeItems(data) {
	if (!data) return [];
	if (Array.isArray(data)) return data;
	if (typeof data === 'object') {
		return Object.values(data);
	}
	return [];
}

/**
 * Normalize any list data shape into the standard format expected by UI.
 * Handles all server response shapes:
 *   - Array of list objects
 *   - { lists: [...] }
 *   - { items: { id: {...}, ... } }
 *   - Flat object map { id: {...}, ... }
 *
 * Ensures every list has canonical field names:
 *   wishlist_id, wishlist_name, wishlist_desc, wishlist_items, etc.
 */
export function extractListsFromObject(data) {
	if (!data) return [];
	let rawLists = null;
	if (Array.isArray(data)) {
		rawLists = data;
	} else if (typeof data === 'object') {
		if (Array.isArray(data.lists)) {
			rawLists = data.lists;
		} else if (data.items && typeof data.items === 'object') {
			rawLists = Array.isArray(data.items) ? data.items : Object.values(data.items);
		} else {
			// Flat object map — filter out non-object values (like 'count')
			rawLists = Object.values(data).filter(
				v => v && typeof v === 'object' && !Array.isArray(v)
			);
		}
	}
	if (!rawLists || !Array.isArray(rawLists)) return [];
	return rawLists.map(item => {
		if (!item || typeof item !== 'object') return null;
		return {
			...item,
			wishlist_id: String(item.wishlist_id ?? item.id ?? ''),
			wishlist_name: item.wishlist_name ?? item.name ?? '',
			wishlist_desc: item.wishlist_desc ?? item.description ?? '',
			wishlist_privacy: Number(item.wishlist_privacy ?? item.privacy ?? 0),
			wishlist_items: normalizeItems(item.wishlist_items ?? item.items ?? []),
			wishlist_url: item.wishlist_url ?? item.url ?? '',
			wishlist_image: item.wishlist_image ?? item.image ?? '',
			is_default: item.is_default ?? false,
			count: Number(item.count ?? 0),
		};
	}).filter(Boolean);
}
